import {
  ApplyStatusList,
  statusTextForConsole,
} from "../../application/application-entity";
import { KintoneDropDownFormField, KintoneFormField } from "../kintone";
import { MetadataFieldMapping } from "../kintone-export-setting";
import { KintoneExportMetadataFieldCorrespondences } from "./metadata-correspondence";
import { ElementFieldMappingValidationErrorCode } from "./validation";

export type ValidateMetadataFieldMappingProps = {
  mapping: MetadataFieldMapping;
  kintoneFields: Record<KintoneFormField["code"], KintoneFormField>;
  usedKintoneFieldCode?: Set<KintoneFormField["code"]>;
};

export type MetadataFieldMappingValidationResult = {
  errors: MetadataFieldMappingValidationError[];
  usedKintoneFieldCode: Set<KintoneFormField["code"]>;
};

export type MetadataFieldMappingValidationError = {
  mappingKey: keyof MetadataFieldMapping;
  errorCode: ElementFieldMappingValidationErrorCode;
};

export const validateMetadataFieldMapping = ({
  mapping,
  kintoneFields,
  usedKintoneFieldCode: usedKintoneFieldCodeArg,
}: ValidateMetadataFieldMappingProps): MetadataFieldMappingValidationResult => {
  const errors: MetadataFieldMappingValidationError[] = [];

  const usedKintoneFieldCode: Set<KintoneFormField["code"]> =
    usedKintoneFieldCodeArg ?? new Set();

  Object.keys(mapping).forEach((key) => {
    const typedKey = key as keyof MetadataFieldMapping;
    const value = mapping[typedKey];
    if (!value) {
      //手続き名とステータスの連携は任意とする。
      if (
        typedKey === "procedureNameFieldCode" ||
        typedKey === "submissionStatusFieldCode"
      ) {
        return;
      }

      errors.push({
        mappingKey: typedKey,
        errorCode: "KINTONE_FIELD_CODE_EMPTY",
      });
      return;
    }

    const kintoneField = kintoneFields[value];
    if (!kintoneField) {
      errors.push({
        mappingKey: typedKey,
        errorCode: "KINTONE_FIELD_NOT_FOUND",
      });
      return;
    }

    if (usedKintoneFieldCode.has(value)) {
      errors.push({
        mappingKey: typedKey,
        errorCode: "KINTONE_FIELD_ALREADY_USED",
      });
      return;
    }

    const acceptedFieldTypes =
      KintoneExportMetadataFieldCorrespondences[typedKey];
    if (
      acceptedFieldTypes.every(
        (acceptedType) => acceptedType !== kintoneField.type,
      )
    ) {
      errors.push({
        mappingKey: typedKey,
        errorCode: "TYPE_MISMATCH",
      });
      return;
    }

    // status の場合は値の一致も見る。
    if (typedKey === "submissionStatusFieldCode") {
      if (
        !matchStatusField(
          // KintoneExportMetadataFieldCorrespondences を確認しているので
          // キャストして問題ない。
          kintoneField as KintoneDropDownFormField,
        )
      ) {
        errors.push({
          mappingKey: typedKey,
          errorCode: "OPTION_MISMATCH",
        });
        return;
      }
    }

    usedKintoneFieldCode.add(value);
  });

  return {
    errors,
    usedKintoneFieldCode,
  };
};

// 申請ステータスの連携先として option の設定が正しいかを確認する。
// TODO: 「アンケート」を入力ソースとして拡張する場合は、うまく対応すること。
const matchStatusField = ({ options }: KintoneDropDownFormField): boolean => {
  const smartApplyStatusSet = new Set(
    ApplyStatusList.map((v) => statusTextForConsole(v)),
  );

  const kintoneOptionsSet = new Set(Object.keys(options));

  if (smartApplyStatusSet.size !== kintoneOptionsSet.size) {
    return false;
  }

  return Array.from(kintoneOptionsSet).every((option) =>
    smartApplyStatusSet.has(option),
  );
};
